import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { aiaAwardsQueryOptions } from "@/lib/aia-awards.queries";
import { boardRosterQueryOptions } from "@/lib/board-snippet.queries";
import { newsQueryOptions } from "@/lib/news.queries";
import { heroSlidesQueryOptions } from "@/lib/slides.queries";
import { researchProductsQueryOptions } from "@/lib/store.queries";
import { BRAND } from "./-welcome/content";
import { HomepageV1 } from "./-homepages/HomepageV1";
import { HomepageV2 } from "./-homepages/HomepageV2";
import { isVersion, VERSION_DEFAULT, type HomepageVersion } from "./-homepages/shared";

/**
 * /homepages — two homepage candidates side by side, switched from a control
 * in the hero (the same device the /sample page uses for its video picks):
 *
 *   1 Slideshow · the current hero slideshow, /welcome content re-laid in the
 *                 /sample layout vocabulary
 *   2 Video     · the /sample video hero with its CTA pointed at Strategic
 *                 Research, then research → newsletter → advisory board → awards
 *
 * `?version=1|2` deep-links a candidate (the `v` name is taken: it is the
 * theme preview override). Both candidates read the same live data as /welcome.
 */
export const Route = createFileRoute("/homepages")({
  validateSearch: (s: Record<string, unknown>): { version?: HomepageVersion } => {
    const n =
      typeof s.version === "string" || typeof s.version === "number"
        ? Number(s.version)
        : undefined;
    return { version: isVersion(n) ? n : undefined };
  },
  head: () => ({
    meta: [
      { title: "Homepages — Medical Insight" },
      {
        name: "description",
        content: `Two homepage candidates for Medical Insight. ${BRAND.short}`,
      },
    ],
  }),
  loader: async ({ context }) => {
    // Warm every cache the candidates read. Failures are non-fatal: each
    // section renders empty rather than blocking the page (and the prerenderer
    // must never be handed a Suspense fallback).
    await Promise.allSettled([
      context.queryClient.ensureQueryData(researchProductsQueryOptions()),
      context.queryClient.ensureQueryData(newsQueryOptions()),
      context.queryClient.ensureQueryData(heroSlidesQueryOptions()),
      context.queryClient.ensureQueryData(aiaAwardsQueryOptions()),
      context.queryClient.ensureQueryData(boardRosterQueryOptions()),
    ]);
  },
  component: HomepagesPage,
});

function HomepagesPage() {
  const { version: fromUrl } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [version, setVersion] = useState<HomepageVersion>(fromUrl ?? VERSION_DEFAULT);

  // Back/forward (or a pasted link) changes the URL; follow it.
  useEffect(() => {
    if (fromUrl && fromUrl !== version) setVersion(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromUrl]);

  const select = (v: HomepageVersion) => {
    setVersion(v);
    void navigate({ search: { version: v }, replace: true, resetScroll: false });
  };

  return version === 2 ? (
    <HomepageV2 version={version} onSelect={select} />
  ) : (
    <HomepageV1 version={version} onSelect={select} />
  );
}
